import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, ArrowDown, Brain, Flame, Gamepad2, Gift, Check, X,
  Footprints, Activity, Bike, Dumbbell, Flower2, Apple, Droplet, Moon, HeartPulse,
  Sparkles, BookOpen, Users, CalendarDays, Smartphone, Receipt, Ticket,
  ShieldCheck, BadgePercent, Heart, Watch, Cpu, LineChart, Lightbulb, Target,
  Zap, Trophy, Repeat, Home as HomeIcon, Building2, Globe2, Lock, Quote, TrendingUp,
} from "lucide-react";
import { ASSETS } from "../lib/assets";
import { Overline, Reveal } from "../components/Section";

const comparison = [
  { old: "Count Steps", neu: "Build Healthy Habits" },
  { old: "Track Activity", neu: "Transform Lifestyle" },
  { old: "Show Numbers", neu: "Inspire Positive Change" },
  { old: "Static Goals", neu: "AI Personalized Goals" },
  { old: "Limited Motivation", neu: "Continuous Motivation" },
  { old: "No Rewards", neu: "Meaningful Digital Rewards" },
  { old: "Individual Experience", neu: "Community & Challenges" },
  { old: "Fitness Tracking", neu: "Habit Transformation" },
  { old: "Activity Logs", neu: "Personal Growth Journey" },
  { old: "Temporary Engagement", neu: "Lifelong Healthy Habits" },
];

const problems = ["Walk More", "Exercise", "Eat Better", "Sleep Better", "Stay Hydrated", "Reduce Stress", "Maintain Healthy Habits"];

const pillars = [
  {
    i: Brain, t: "AI Intelligence", c: "from-emerald-500 to-teal-500",
    items: ["Personalized Wellness Coach", "Smart Recommendations", "Adaptive Goals", "Health Insights", "Predictive Analytics"],
  },
  {
    i: Flame, t: "Habit Science", c: "from-sky-500 to-blue-500",
    items: ["Daily Streaks", "Micro Achievements", "Behavioral Reinforcement", "Habit Formation", "Long-Term Consistency"],
  },
  {
    i: Gamepad2, t: "Gamification", c: "from-violet-500 to-purple-500",
    items: ["Challenges", "Leaderboards", "Badges", "Milestones", "Seasonal Events"],
  },
  {
    i: Gift, t: "Digital Rewards", c: "from-amber-500 to-orange-500",
    items: ["Fit Points", "Everyday Essentials", "Lifestyle Experiences", "Wellness Benefits", "Partner Rewards"],
  },
];

const beyondWalking = [
  { i: Footprints, t: "Walking" }, { i: Activity, t: "Running" }, { i: Bike, t: "Cycling" },
  { i: Dumbbell, t: "Gym Workouts" }, { i: Flower2, t: "Yoga" }, { i: Sparkles, t: "Meditation" },
  { i: Apple, t: "Nutrition" }, { i: Droplet, t: "Hydration" }, { i: Moon, t: "Sleep" },
  { i: HeartPulse, t: "Mental Wellness" }, { i: Lightbulb, t: "Mindfulness" }, { i: Check, t: "Healthy Daily Habits" },
  { i: BookOpen, t: "Learning" }, { i: Users, t: "Community Activities" }, { i: CalendarDays, t: "Future Wellness Programs" },
];

const engine = [
  { i: Watch, t: "Health Connect & Wearables" },
  { i: Cpu, t: "AI Data Processing" },
  { i: LineChart, t: "Behavior Analysis" },
  { i: Lightbulb, t: "Personal Wellness Insights" },
  { i: Target, t: "Habit Recommendations" },
  { i: Zap, t: "Motivation Engine" },
  { i: Gift, t: "Fit Rewards" },
  { i: HeartPulse, t: "Healthier Lifestyle" },
];

const rewardCategories = [
  {
    i: Smartphone, t: "Everyday Essentials",
    d: "Everyday digital services that simplify your daily life.",
    ex: ["Mobile Services", "Utility Payments", "Essential Digital Services"],
  },
  {
    i: Ticket, t: "Lifestyle & Experiences",
    d: "Celebrate your healthy journey with entertainment and memorable experiences.",
    ex: ["Movies", "Travel Experiences", "Events", "Lifestyle Benefits"],
  },
  {
    i: Heart, t: "Health & Wellness",
    d: "Invest back into your health with wellness-focused rewards and healthcare benefits.",
    ex: ["Wellness Offers", "Healthcare Benefits", "Insurance Services"],
  },
  {
    i: Gift, t: "Shopping & Gift Rewards",
    d: "Exclusive savings, cashback opportunities, and digital gift rewards from trusted partners.",
    ex: [],
  },
  {
    i: Sparkles, t: "Growing Rewards Ecosystem",
    d: "Our partner ecosystem continues to expand as the Fit2Fin community grows.",
    ex: ["Many More Coming Soon..."],
  },
];

const flywheel = [
  "Healthy Action", "AI Analysis", "Personal Motivation", "Fit Points Rewards",
  "Positive Reinforcement", "Better Daily Habits", "Improved Health & Well-being",
  "Continued Engagement", "Healthier Families & Communities",
];

const impact = [
  { i: Heart, t: "Healthier Individuals" }, { i: HomeIcon, t: "Healthier Families" },
  { i: Building2, t: "Healthier Workplaces" }, { i: ShieldCheck, t: "Reduced Lifestyle Diseases" },
  { i: Brain, t: "Better Mental Wellness" }, { i: TrendingUp, t: "Higher Productivity" },
  { i: Users, t: "Community Wellness" }, { i: Globe2, t: "A Healthier India" },
];

const phases = [
  { p: "Phase 1", items: ["Step Tracking", "Fit Points", "Health Connect Integration"] },
  { p: "Phase 2", items: ["Nutrition", "Hydration", "Sleep Tracking", "Habit Challenges"] },
  { p: "Phase 3", items: ["Family Wellness", "Corporate Wellness", "Schools & Colleges", "Community Programs"] },
  { p: "Phase 4", items: ["AI Health Coach", "Preventive Healthcare", "Insurance Integrations", "Global Expansion"] },
];

const whyChoose = [
  { i: Brain, t: "AI Powered" }, { i: Repeat, t: "Habit Driven" },
  { i: Gift, t: "Reward Focused" }, { i: Lock, t: "Privacy First" },
  { i: ShieldCheck, t: "Secure" }, { i: Users, t: "Community Powered" },
  { i: BadgePercent, t: "Financially Rewarding" }, { i: Sparkles, t: "Future Ready" },
];

export default function Fit2FinDifferent() {
  useEffect(() => {
    document.title = "Why Fit2Fin is Different — India's First Habit Platform | PJSRS Infotech";
    return () => { document.title = "PJSRS Infotech Private Limited"; };
  }, []);

  // Hidden while product details are locked — redirects to the Fit2Fin coming-soon page
  const SHOW_DETAILS = false;
  if (!SHOW_DETAILS) return <Navigate to="/fit2fin" replace />;

  return (
    <div data-testid="fit2fin-different-page" className="bg-slate-100 text-slate-900">
      {/* HERO */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-br from-emerald-100 via-slate-100 to-sky-100">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-emerald-400/30 blur-[120px]" />
          <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-sky-400/30 blur-[120px]" />
          <motion.div
            animate={{ y: [0, -14, 0] }} transition={{ duration: 6, repeat: Infinity }}
            className="hidden lg:block absolute top-40 right-24 rounded-2xl bg-white shadow-xl ring-1 ring-slate-100 p-4"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Streak</div>
            <div className="font-display text-2xl font-black text-slate-900 mt-1">21 days</div>
            <div className="text-xs text-emerald-600 font-semibold mt-1">+120 Fit Points</div>
          </motion.div>
          <motion.div
            animate={{ y: [0, 12, 0] }} transition={{ duration: 7, repeat: Infinity }}
            className="hidden lg:block absolute bottom-32 right-56 rounded-2xl bg-slate-900 text-white shadow-xl p-4"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">AI Coach</div>
            <div className="text-sm font-semibold mt-1">"Great pace! 2k steps to goal."</div>
          </motion.div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/fit2fin" data-testid="different-back-fit2fin" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900">
              ← Back to Fit2Fin
            </Link>
          </Reveal>
          <Reveal delay={0.03}>
            <div className="mt-5 inline-flex rounded-full border border-slate-300 bg-white p-1 text-sm shadow-sm" data-testid="different-tabs">
              <Link to="/fit2fin" data-testid="different-tab-overview" className="rounded-full px-5 py-2 text-slate-600 hover:text-slate-900 transition-colors">
                Overview
              </Link>
              <span className="rounded-full bg-slate-900 text-white px-5 py-2 font-semibold" data-testid="different-tab-different">Why Fit2Fin is Different</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-white border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm" data-testid="different-badge">
              🇮🇳 India's First Habit Platform
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h1 className="mt-6 font-display font-black text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tighter">
              Why Fit2Fin<sup className="text-[0.3em] font-bold align-super text-slate-500">™</sup> is <span className="text-gradient-fit">Different</span>
            </h1>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-6 font-display text-xl md:text-2xl font-bold text-slate-700 max-w-2xl">
              We don't just count steps. We help people build healthier lives — one habit at a time.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-5 max-w-2xl text-lg text-slate-600 leading-relaxed">
              Fit2Fin is an AI-powered habit and wellness platform that transforms everyday healthy actions
              into meaningful rewards, creating lasting lifestyle changes through motivation, technology, and
              behavioral science.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.25em] text-emerald-700">
              Turn Healthy Habits into Meaningful Rewards.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="different-cta-journey"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 text-white px-7 py-4 text-sm font-bold hover:opacity-90 transition shadow-[0_10px_40px_rgba(16,185,129,0.35)] hover:-translate-y-0.5"
              >
                Start Your Healthy Journey <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/fit2fin"
                data-testid="different-cta-explore"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-sm font-medium hover:bg-slate-50 transition"
              >
                Explore Fit2Fin
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 1 — COMPARISON */}
      <section className="relative py-24 bg-slate-100" data-testid="different-comparison">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Overline color="text-emerald-600">Traditional Fitness Apps vs Fit2Fin</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Not a tracker. <span className="text-gradient-fit">A transformation.</span>
            </h2>
          </div>

          <div className="mt-14 rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
            <div className="grid grid-cols-2 bg-slate-900 text-white">
              <div className="p-5 md:p-6 text-center font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-slate-400">Traditional Fitness Apps</div>
              <div className="p-5 md:p-6 text-center font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-emerald-300 bg-gradient-to-r from-emerald-600/30 to-sky-600/30">Fit2Fin™</div>
            </div>
            {comparison.map((row, i) => (
              <Reveal key={row.old} delay={i * 0.03}>
                <div className={`grid grid-cols-2 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                  <div className="p-4 md:p-5 flex items-center gap-3 border-r border-slate-200">
                    <X className="h-4 w-4 text-slate-300 shrink-0" />
                    <span className="text-sm md:text-base text-slate-500">{row.old}</span>
                  </div>
                  <div className="p-4 md:p-5 flex items-center gap-3 bg-emerald-50/40">
                    <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span className="text-sm md:text-base font-semibold text-slate-900">{row.neu}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-12 rounded-3xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white p-8 md:p-12 text-center">
              <Quote className="h-8 w-8 mx-auto opacity-80" />
              <p className="mt-4 font-display text-2xl md:text-3xl font-bold leading-snug max-w-3xl mx-auto">
                "Healthy habits create healthy lives. Technology should inspire them — not just measure them."
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 2 — PROBLEM */}
      <section className="relative py-24 bg-slate-200/70 border-y border-slate-300/60" data-testid="different-problem">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Overline color="text-emerald-600">The Problem We Solve</Overline>
            <h2 className="mt-4 font-display font-black text-4xl tracking-tighter leading-tight">
              Everyone knows <span className="text-gradient-fit">what</span> to do. Few stay consistent.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Millions of people know they should walk more, exercise, eat better, and sleep well — but most
              lose motivation after only a few weeks.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Why? Because healthy habits provide <strong>long-term benefits</strong>, while unhealthy choices
              often provide <strong>immediate satisfaction</strong>.
            </p>
            <p className="mt-6 font-display text-2xl font-black text-gradient-fit">Fit2Fin changes that.</p>
          </div>
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {problems.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4">
                  <div className="h-6 w-6 rounded-full bg-emerald-500 text-white grid place-items-center shrink-0">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                  <span className="font-medium text-slate-800">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — PHILOSOPHY */}
      <section className="relative py-28 bg-slate-100" data-testid="different-philosophy">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Overline color="text-emerald-600">Our Philosophy</Overline>
          <Reveal>
            <h2 className="mt-6 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[1]">
              Every Healthy Habit<br /><span className="text-gradient-fit">Deserves a Reward.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed">
              Small daily actions become lifelong transformations.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {["Every walk matters.", "Every workout matters.", "Every healthy meal matters.", "Every good night's sleep matters.", "Every positive habit matters."].map((t) => (
                <span key={t} className="rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-8 text-slate-500">
              Fit2Fin exists to recognize, encourage, and reward these everyday achievements.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 4 — AI + HABIT SCIENCE + REWARDS */}
      <section className="relative py-24 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950 text-white overflow-hidden" data-testid="different-pillars">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-emerald-400">AI + Habit Science + Rewards</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Four engines. <span className="text-gradient-fit">One transformation.</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="group rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur p-7 h-full hover:bg-white/[0.07] hover:-translate-y-1 transition">
                  <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${p.c} grid place-items-center text-white shadow-lg`}>
                    <p.i className="h-6 w-6" />
                  </div>
                  <div className="mt-5 font-display text-xl font-black">{p.t}</div>
                  <ul className="mt-4 space-y-2.5">
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — MORE THAN WALKING */}
      <section className="relative py-24 bg-slate-100" data-testid="different-beyond">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Overline color="text-emerald-600">More Than Walking</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Rewarding <span className="text-gradient-fit">healthy living</span> — not just activity.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {beyondWalking.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.03}>
                <div className="group rounded-2xl border border-slate-200 bg-white p-6 text-center hover:border-emerald-300 hover:-translate-y-1 transition h-full">
                  <div className="h-12 w-12 rounded-xl mx-auto bg-emerald-100 text-emerald-600 grid place-items-center group-hover:bg-gradient-to-br group-hover:from-emerald-500 group-hover:to-sky-500 group-hover:text-white transition">
                    <f.i className="h-5 w-5" />
                  </div>
                  <div className="mt-4 text-sm font-semibold text-slate-900">{f.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — AI ENGINE WORKFLOW */}
      <section className="relative py-24 bg-slate-200/70 border-y border-slate-300/60" data-testid="different-engine">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-emerald-600">AI Personal Wellness Engine</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              From data to <span className="text-gradient-fit">healthier lifestyles.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              The more you use Fit2Fin, the more intelligently it supports your wellness journey.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-4">
            {engine.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.06}>
                <div className="relative flex flex-col items-center text-center">
                  <div className="h-14 w-14 rounded-2xl bg-white border border-slate-200 shadow-md grid place-items-center text-emerald-600">
                    <s.i className="h-6 w-6" />
                  </div>
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Step {i + 1}</div>
                  <div className="mt-1 font-semibold text-slate-900 text-sm max-w-[180px]">{s.t}</div>
                  {i < engine.length - 1 && (
                    <ArrowRight className="hidden lg:block absolute top-4 -right-4 h-5 w-5 text-emerald-400" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHY DIGITAL REWARDS MATTER */}
      <section className="relative py-24 bg-slate-100" data-testid="different-rewards">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Overline color="text-emerald-600">Why Digital Rewards Matter</Overline>
            <h2 className="mt-4 font-display font-black text-4xl tracking-tighter leading-tight">
              Statistics don't motivate. <span className="text-gradient-fit">Value does.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Most fitness apps stop after showing statistics. Fit2Fin converts healthy behavior into
              meaningful value — rewards are the motivation, never the destination.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rewardCategories.map((r, i) => (
              <Reveal key={r.t} delay={i * 0.05}>
                <div className={`group rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:-translate-y-1 transition h-full ${i === rewardCategories.length - 1 ? "sm:col-span-2" : ""}`}>
                  <div className="flex items-start gap-4">
                    <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white grid place-items-center shrink-0">
                      <r.i className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-slate-900">{r.t}</div>
                      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{r.d}</p>
                      {r.ex.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {r.ex.map((e) => (
                            <span key={e} className="rounded-full bg-slate-50 border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-500">{e}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 — FLYWHEEL */}
      <section className="relative py-24 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950 text-white overflow-hidden" data-testid="different-flywheel">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Overline color="text-emerald-400">The Fit2Fin Flywheel</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              A self-reinforcing <span className="text-gradient-fit">cycle of wellness.</span>
            </h2>
          </div>

          {/* Circular flywheel — desktop */}
          <div className="hidden lg:block relative mx-auto mt-16 h-[640px] w-[640px]">
            <div className="absolute inset-16 rounded-full border-2 border-dashed border-emerald-500/30" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="h-40 w-40 rounded-full bg-gradient-to-br from-emerald-500 to-sky-500 grid place-items-center text-center shadow-[0_0_60px_rgba(16,185,129,0.4)]">
                <div>
                  <Repeat className="h-7 w-7 mx-auto" />
                  <div className="mt-1 font-display font-black text-lg leading-tight">Fit2Fin<br />Flywheel</div>
                </div>
              </div>
            </div>
            {flywheel.map((f, i) => {
              const angle = (i / flywheel.length) * 2 * Math.PI - Math.PI / 2;
              const x = 50 + 42 * Math.cos(angle);
              const y = 50 + 42 * Math.sin(angle);
              return (
                <Reveal key={f} delay={i * 0.06}>
                  <div
                    className="absolute w-40 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-emerald-500/30 bg-slate-900/90 backdrop-blur px-4 py-3 text-center shadow-lg"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <div className="font-mono text-[10px] text-emerald-400">{String(i + 1).padStart(2, "0")}</div>
                    <div className="text-xs font-semibold leading-snug mt-0.5">{f}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Vertical flywheel — mobile/tablet */}
          <div className="lg:hidden mt-14 max-w-sm mx-auto">
            {flywheel.map((f, i) => (
              <Reveal key={f} delay={i * 0.04}>
                <div className="flex flex-col items-center">
                  <div className="w-full rounded-2xl border border-emerald-500/30 bg-slate-900/90 px-5 py-4 text-center">
                    <div className="font-mono text-[10px] text-emerald-400">{String(i + 1).padStart(2, "0")}</div>
                    <div className="text-sm font-semibold mt-0.5">{f}</div>
                  </div>
                  <ArrowDown className="h-5 w-5 text-emerald-400 my-2" />
                </div>
              </Reveal>
            ))}
            <div className="text-center font-mono text-[10px] uppercase tracking-widest text-emerald-400">↺ returns to Healthy Action</div>
          </div>

          <Reveal delay={0.2}>
            <p className="mt-14 max-w-2xl mx-auto text-center text-slate-300 leading-relaxed">
              The Fit2Fin Flywheel continuously motivates users to build healthier habits through a
              self-reinforcing cycle of AI-driven guidance, rewards, and long-term engagement.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 9 — SOCIAL IMPACT */}
      <section className="relative py-24 bg-slate-100" data-testid="different-impact">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-emerald-600">Social Impact</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Wellness that <span className="text-gradient-fit">ripples outward.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {impact.map((m, i) => (
              <Reveal key={m.t} delay={i * 0.05}>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full">
                  <div className="h-11 w-11 rounded-xl bg-emerald-100 grid place-items-center text-emerald-600">
                    <m.i className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-semibold text-slate-900 text-sm">{m.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10 — ROADMAP */}
      <section className="relative py-24 bg-slate-200/70 border-y border-slate-300/60" data-testid="different-roadmap">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-emerald-600">Future Roadmap</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Built in <span className="text-gradient-fit">phases.</span> Designed for life.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {phases.map((ph, i) => (
              <Reveal key={ph.p} delay={i * 0.08}>
                <div className="relative rounded-3xl border border-slate-200 bg-white p-7 h-full">
                  <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 text-white px-4 py-1.5 text-xs font-bold">
                    {ph.p}
                  </div>
                  <ul className="mt-5 space-y-2.5">
                    {ph.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11 — WHY CHOOSE */}
      <section className="relative py-24 bg-slate-100" data-testid="different-why-choose">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Overline color="text-emerald-600">Why Choose Fit2Fin</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Everything a habit platform <span className="text-gradient-fit">should be.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {whyChoose.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.05}>
                <div className="group rounded-2xl border border-slate-200 bg-white p-6 text-center hover:border-emerald-300 hover:-translate-y-1 transition h-full">
                  <div className="h-12 w-12 rounded-xl mx-auto bg-emerald-100 text-emerald-600 grid place-items-center group-hover:bg-gradient-to-br group-hover:from-emerald-500 group-hover:to-sky-500 group-hover:text-white transition">
                    <w.i className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-display font-bold text-slate-900">{w.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL BANNER */}
      <section className="relative py-28 bg-gradient-to-br from-emerald-500 to-sky-500 text-white" data-testid="different-final-banner">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-4 py-2 text-sm font-semibold backdrop-blur">
            🇮🇳 India's First Habit Platform
          </div>
          <h2 className="mt-8 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[1.02]">
            Every Step Counts.<br />Every Habit Rewards.<br />Every Life Matters.
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-emerald-50/90 text-lg">
            Fit2Fin is building a future where healthy living becomes a rewarding lifestyle — not a temporary
            challenge.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              data-testid="different-banner-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-emerald-700 px-7 py-4 text-sm font-bold hover:bg-emerald-50 transition shadow-xl"
            >
              Start Your Healthy Journey <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/fit2fin"
              data-testid="different-banner-explore"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900/30 text-white border border-white/30 px-7 py-4 text-sm font-medium hover:bg-slate-900/50 transition backdrop-blur"
            >
              Explore Fit2Fin
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
